import { Popup } from "maplibre-gl"
import type {
  GeoJSONSource,
  Map as MapLibreMap,
  MapLayerMouseEvent,
} from "maplibre-gl"
import { getAboveRouteBeforeId } from "./aboveRouteLayerOrder"
import { svgToCanvas } from "./mapUtils"
import { TrafficIncident } from "./routeTraffic"
import {
  incidentIconId,
  incidentTimes,
  incidentTypeLabel,
} from "./trafficIncidents"

export const TRAFFIC_INCIDENTS_SOURCE_ID = "traffic-incidents-source"
export const TRAFFIC_INCIDENTS_LAYER_ID = "traffic-incidents-layer"

// Black pictograms for the warning triangles: lucide shapes (24x24 viewBox)
const PICTOGRAMS: Record<string, string> = {
  "traffic-incident-construction":
    '<rect x="2" y="6" width="20" height="8" rx="1"/><path d="M17 14v7"/><path d="M7 14v7"/><path d="M17 3v3"/><path d="M7 3v3"/><path d="M10 14 2.3 6.3"/><path d="m14 6 7.7 7.7"/><path d="m8 6 8 8"/>',
  "traffic-incident-accident":
    '<path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8"/><path d="M7 14h.01"/><path d="M17 14h.01"/><rect width="18" height="8" x="3" y="10" rx="2"/><path d="M5 18v2"/><path d="M19 18v2"/>',
  "traffic-incident-laneRestriction":
    '<path d="m8 6 4-4 4 4"/><path d="M12 2v10.3a4 4 0 0 1-1.172 2.872L4 22"/><path d="m20 22-5-5"/>',
  "traffic-incident-congestion":
    '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>',
  "traffic-incident-generic": '<path d="M12 7v6"/><path d="M12 17h.01"/>',
}

const SIGN_RED = "#D7261E"
const SHADOW =
  '<filter id="s" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="0.5" stdDeviation="0.6" flood-opacity="0.35"/></filter>'

/**
 * European road signs, as truck navigation shows them: a white warning
 * triangle with a red edge and a black pictogram, and the round red
 * "no entry" sign for a closed road. They sit centered on the route line.
 */
export function incidentIconSvg(iconId: string): string {
  const open = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"><defs>${SHADOW}</defs><g filter="url(#s)">`
  if (iconId === "traffic-incident-roadClosure") {
    return `${open}<circle cx="12" cy="12" r="11" fill="#fff"/><circle cx="12" cy="12" r="10" fill="${SIGN_RED}"/><rect x="5.5" y="10.4" width="13" height="3.2" fill="#fff"/></g></svg>`
  }
  const pictogram = PICTOGRAMS[iconId] ?? PICTOGRAMS["traffic-incident-generic"]
  const scale = iconId === "traffic-incident-generic" ? 0.5 : 0.42
  return `${open}<path d="M12 1.2 23.2 21.5H.8Z" fill="#fff" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/><path d="M12 2.6 22 20.6H2Z" fill="#fff" stroke="${SIGN_RED}" stroke-width="2.4" stroke-linejoin="round"/><g transform="translate(12 14.2) scale(${scale}) translate(-12 -12)" fill="none" stroke="#111" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">${pictogram}</g></g></svg>`
}

const ICON_IDS = [...Object.keys(PICTOGRAMS), "traffic-incident-roadClosure"]

const EMPTY = { type: "FeatureCollection" as const, features: [] }

export type IncidentProperties = {
  icon: string
  description: string
  label: string
  criticality: string
  valid_until: string
  // PTV only; 0 and "" when absent (GeoJSON properties are plain values)
  delay_s: number
  arrives_at: string
}

/** Incident icons along the stored route, with a popup on hover or click. */
export default class TrafficIncidentsLayer {
  private popup: Popup | null = null
  private popupPinned = false
  private pending: TrafficIncident[] | null = null

  constructor(readonly map: MapLibreMap) {}

  setIncidents(incidents: TrafficIncident[]): void {
    if (!this.map.isStyleLoaded()) {
      // Only the latest call matters once the style is in
      if (this.pending === null) {
        this.map.once("idle", () => {
          // Already applied by a later call, or the layer was removed
          if (this.pending === null) {
            return
          }
          const latest = this.pending
          this.pending = null
          this.setIncidents(latest)
        })
      }
      this.pending = incidents
      return
    }
    this.pending = null

    this.ensureIcons()
    if (!this.map.getSource(TRAFFIC_INCIDENTS_SOURCE_ID)) {
      this.map.addSource(TRAFFIC_INCIDENTS_SOURCE_ID, {
        type: "geojson",
        data: EMPTY,
      })
    }
    if (!this.map.getLayer(TRAFFIC_INCIDENTS_LAYER_ID)) {
      this.map.addLayer(
        {
          id: TRAFFIC_INCIDENTS_LAYER_ID,
          type: "symbol",
          source: TRAFFIC_INCIDENTS_SOURCE_ID,
          layout: {
            "icon-image": ["get", "icon"],
            "icon-anchor": "center",
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          },
        },
        getAboveRouteBeforeId(this.map),
      )
      this.map.on("mouseenter", TRAFFIC_INCIDENTS_LAYER_ID, this.handleEnter)
      this.map.on("mousemove", TRAFFIC_INCIDENTS_LAYER_ID, this.handleMove)
      this.map.on("mouseleave", TRAFFIC_INCIDENTS_LAYER_ID, this.handleLeave)
      this.map.on("click", TRAFFIC_INCIDENTS_LAYER_ID, this.handleClick)
    }

    const source = this.map.getSource(TRAFFIC_INCIDENTS_SOURCE_ID) as
      | GeoJSONSource
      | undefined
    source?.setData({
      type: "FeatureCollection",
      features: incidents.map((incident) => ({
        type: "Feature",
        properties: incidentProperties(incident),
        geometry: { type: "Point", coordinates: [incident.lng, incident.lat] },
      })),
    })
  }

  remove(): void {
    this.pending = null
    this.closePopup()
    this.map.off("mouseenter", TRAFFIC_INCIDENTS_LAYER_ID, this.handleEnter)
    this.map.off("mousemove", TRAFFIC_INCIDENTS_LAYER_ID, this.handleMove)
    this.map.off("mouseleave", TRAFFIC_INCIDENTS_LAYER_ID, this.handleLeave)
    this.map.off("click", TRAFFIC_INCIDENTS_LAYER_ID, this.handleClick)
    if (this.map.getLayer(TRAFFIC_INCIDENTS_LAYER_ID)) {
      this.map.removeLayer(TRAFFIC_INCIDENTS_LAYER_ID)
    }
    if (this.map.getSource(TRAFFIC_INCIDENTS_SOURCE_ID)) {
      this.map.removeSource(TRAFFIC_INCIDENTS_SOURCE_ID)
    }
  }

  private ensureIcons() {
    ICON_IDS.forEach((iconId) => {
      if (!this.map.hasImage(iconId)) {
        this.map.addImage(
          iconId,
          svgToCanvas(this.map, incidentIconSvg(iconId)),
          {
            pixelRatio: 2,
          },
        )
      }
    })
  }

  private handleEnter = (event: MapLayerMouseEvent) => {
    this.handleMove()
    if (!this.popupPinned) {
      this.showPopup(event, false)
    }
  }

  // The normal arrow over an icon. The directions library sets its own
  // cursor (a hand) on every mouse move; this handler is added after it, so
  // it runs later and wins.
  private handleMove = () => {
    this.map.getCanvas().style.cursor = "default"
  }

  private handleLeave = () => {
    this.map.getCanvas().style.cursor = ""
    if (!this.popupPinned) {
      this.closePopup()
    }
  }

  private handleClick = (event: MapLayerMouseEvent) => {
    this.showPopup(event, true)
  }

  /** Moves the map to the incident and opens its box (from the incident list). */
  showIncident(incident: TrafficIncident): void {
    const lngLat: [number, number] = [incident.lng, incident.lat]
    this.map.flyTo({ center: lngLat, zoom: Math.max(this.map.getZoom(), 12) })
    this.openPopup(lngLat, incidentProperties(incident), true)
  }

  private showPopup(event: MapLayerMouseEvent, pinned: boolean) {
    const feature = event.features?.[0]
    if (!feature) {
      return
    }
    const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates
    this.openPopup([lng, lat], feature.properties as IncidentProperties, pinned)
  }

  private openPopup(
    lngLat: [number, number],
    properties: IncidentProperties,
    pinned: boolean,
  ) {
    this.closePopup()
    this.popupPinned = pinned
    this.popup = new Popup({
      closeButton: pinned,
      closeOnClick: true,
      focusAfterOpen: false,
      // Clear of the 24 px sign whichever side MapLibre opens the popup on
      offset: 14,
    })
      .setLngLat(lngLat)
      .setDOMContent(incidentPopupContent(properties))
      .addTo(this.map)
    this.popup.on("close", () => {
      this.popupPinned = false
    })
  }

  private closePopup() {
    this.popup?.remove()
    this.popup = null
    this.popupPinned = false
  }
}

function incidentProperties(incident: TrafficIncident): IncidentProperties {
  return {
    icon: incidentIconId(incident.type),
    description: incident.description ?? "",
    label: incidentTypeLabel(incident.type),
    criticality: incident.criticality ?? "",
    valid_until: incident.valid_until ?? "",
    delay_s: incident.delay_s ?? 0,
    arrives_at: incident.arrives_at ?? "",
  }
}

export function incidentPopupContent(
  properties: IncidentProperties,
): HTMLElement {
  // textContent only: the texts come from HERE and PTV
  const root = document.createElement("div")
  // Popups have no padding of their own here (utilsCss.module.css); pr-7
  // keeps the title clear of the close button of a pinned popup
  root.className =
    "rounded-lg bg-white p-3 pr-7 text-sm text-slate-900 shadow-lg dark:bg-gray-800 dark:text-white"

  const title = document.createElement("div")
  title.className = "font-semibold"
  title.textContent = properties.description || properties.label
  root.appendChild(title)

  const details = [properties.label, properties.criticality]
    .filter(Boolean)
    .join(" · ")
  const detailLine = document.createElement("div")
  detailLine.className = "text-gray-600 dark:text-gray-300"
  detailLine.textContent = details
  root.appendChild(detailLine)

  const times = incidentTimes(properties)
  if (times.length) {
    const timesLine = document.createElement("div")
    timesLine.className = "text-gray-600 dark:text-gray-300"
    timesLine.textContent = times.join(" · ")
    root.appendChild(timesLine)
  }
  return root
}
